import { useEffect, useState } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import { useNavigate, useParams } from 'react-router-dom';

import { Breadcrumbs } from '#client/components/Breadcrumbs';
import { DisableLayer } from '#client/components/DisableLayer';
import { FocusGrid, HubMainWrapperStyled, HubContainerGridStyled } from '#client/components/Grid';
import Notifications from '#client/components/Notifications';
import PayeeInfo from '#client/components/PayeeInfo';
import PaymentContainer from '#client/components/PaymentContainer';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { EMPTY_PAYMENT_DESCRIPTION, PAYMENT_LIST_STATUS } from '#client/constants/transaction';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useCounterpartyDetails from '#client/hooks/useCounterpartyDetails';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useScrollTo from '#client/hooks/useScrollTo';
import SharedNavigateModals from '#client/pages/SharedNavigateModals';

import { BreadcrumbWrapStyled } from './PayeeTransactionHistory.styles';

const { isWebView } = WebView;

export default function PayeeTransactionHistory() {
  const navigate = useNavigate();
  const { counterPartyId } = useParams();
  const { isOpeningModal } = useCheckOpenModal();
  const { isDisable } = useDisableDashboardContext();

  const { counterPartyData, fetchingCounterparty, counterpartyError } = useCounterpartyDetails({
    counterpartyId: counterPartyId
  });

  const {
    name,
    bank,
    accountNumber,
    status,
    accountType,
    connectedType,
    fullAccountNumber,
    wire,
    isWireSupported,
    isACHSupported,
    fullRoutingNumber,
    nickname,
    firstName,
    lastName,
    routingNumber,
    type
  } = counterPartyData || {};

  const [paymentListStatus, setPaymentListStatus] = useState(PAYMENT_LIST_STATUS.LOADING);

  useEffect(() => {
    const isRedirect =
      counterpartyError ||
      (connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID && !fetchingCounterparty);

    if (isRedirect) {
      navigate('..', { relative: 'path', replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedType, counterpartyError, fetchingCounterparty]);

  useScrollTo(0, 0);

  return isOpeningModal ? (
    <SharedNavigateModals />
  ) : (
    <HubContainerGridStyled>
      <FocusGrid>
        <HubMainWrapperStyled>
          {!isWebView && (
            <BreadcrumbWrapStyled>
              <Breadcrumbs
                items={[
                  {
                    to: '../../finances',
                    label: 'Finances'
                  },
                  {
                    to: '../dashboard',
                    label: 'Banking'
                  },
                  {
                    to: '../payees',
                    label: 'Payees'
                  },
                  {
                    label: name
                  }
                ]}
              />
            </BreadcrumbWrapStyled>
          )}
          <Notifications />
          <Box position="relative">
            {isDisable && <DisableLayer aria-label="Disable layer" />}
            <PayeeInfo
              accountNumber={accountNumber}
              accountType={accountType}
              bank={bank}
              connectedType={connectedType}
              firstName={firstName}
              fullAccountNumber={fullAccountNumber}
              fullRoutingNumber={fullRoutingNumber}
              id={counterPartyId}
              isACHSupported={isACHSupported}
              isWireSupported={isWireSupported}
              lastName={lastName}
              loading={fetchingCounterparty}
              name={name}
              nickname={nickname}
              paymentStatus={paymentListStatus}
              routingNumber={routingNumber}
              status={status}
              type={type}
              wire={wire}
            />
            <PaymentContainer
              callback={setPaymentListStatus}
              counterparty={{
                counterpartyId: counterPartyId,
                bank,
                connectedType,
                accountNumber,
                fullAccountNumber
              }}
              emptyText={EMPTY_PAYMENT_DESCRIPTION.PAYEE}
            />
          </Box>
        </HubMainWrapperStyled>
      </FocusGrid>
    </HubContainerGridStyled>
  );
}
